import { useEffect, useState } from 'react';
import { puwg2000ToWgs84, detectZoneFromY, type Puwg2000Zone } from '@/lib/puwg2000';
import { SearchableSelect } from '@/components/SearchableSelect';

/**
 * Sanityzuje wpisywaną liczbę dziesiętną: polski przecinek → kropka, usuwa wszystko poza
 * cyframi/kropką/wiodącym minusem, dopuszcza tylko jedną kropkę. Zachowuje częściowe wpisy
 * ("-", "52.", "") żeby nie blokować pisania. To zarazem maska (znak po znaku) współrzędnych.
 */
export function sanitizeDecimalInput(raw: string): string {
  let s = raw.replace(/,/g, '.').replace(/[^0-9.\-]/g, '');
  const neg = s.startsWith('-');
  s = s.replace(/-/g, '');
  const firstDot = s.indexOf('.');
  if (firstDot >= 0) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');
  }
  return (neg ? '-' : '') + s;
}

type CoordValidity = { level: 'ok' | 'warn' | 'error'; msg?: string };

/** Waliduje pojedynczą współrzędną WGS84. Twardy zakres = ±90/±180; poza obszarem PL = ostrzeżenie. */
export function coordValidity(str: string, kind: 'lat' | 'lng'): CoordValidity {
  const partial = str === '' || str === '-' || str === '.' || str === '-.';
  if (partial) return { level: 'ok' };
  const n = Number(str);
  if (!Number.isFinite(n)) return { level: 'error', msg: 'Nieprawidłowa liczba.' };
  const [hardMin, hardMax] = kind === 'lat' ? [-90, 90] : [-180, 180];
  if (n < hardMin || n > hardMax) return { level: 'error', msg: `Poza zakresem ${hardMin}…${hardMax}°.` };
  const [plMin, plMax] = kind === 'lat' ? [48, 56] : [13, 25];
  if (n < plMin || n > plMax) return { level: 'warn', msg: 'Poza obszarem Polski — sprawdź kolejność i znak.' };
  return { level: 'ok' };
}

interface Props {
  /** Aktualna szerokość geograficzna (WGS84) lub null/undefined gdy puste. */
  latitude: number | null | undefined;
  /** Aktualna długość geograficzna (WGS84). */
  longitude: number | null | undefined;
  /** Callback po zmianie — backend zawsze dostaje WGS84 niezależnie od formatu inputu. */
  onChange: (lat: number | null, lng: number | null) => void;
  /** Inline (kreator) czy compact (modal). Ustawia układ pól. */
  layout?: 'inline' | 'stacked';
  /** Custom label dla pól (np. "Współrzędne otworu"). Default: "Współrzędne". */
  label?: string;
  disabled?: boolean;
}

/**
 * Wspólny komponent wprowadzania współrzędnych — toggle między WGS84 (lat/lng w stopniach)
 * i PUWG 2000 (X/Y w metrach z numerem strefy). Konwersja w przeglądarce przez proj4.
 * Backend dostaje zawsze pary WGS84 (lat, lng).
 *
 * Sanity check: po konwersji weryfikujemy że punkt mieści się w przybliżonym zakresie
 * terytorium Polski (lat 48-56 / lng 13-25). Inaczej pokazujemy ostrzeżenie bez zapisu.
 */
export function CoordsInput({
  latitude, longitude, onChange, layout = 'inline', label = 'Współrzędne', disabled,
}: Props) {
  const [coordFormat, setCoordFormat] = useState<'WGS84' | 'PUWG_2000'>('WGS84');
  const [latStr, setLatStr] = useState<string>(latitude != null ? String(latitude) : '');
  const [lngStr, setLngStr] = useState<string>(longitude != null ? String(longitude) : '');
  const [puwgZone, setPuwgZone] = useState<Puwg2000Zone | 'AUTO'>('AUTO');
  const [puwgX, setPuwgX] = useState<string>('');
  const [puwgY, setPuwgY] = useState<string>('');
  const [puwgError, setPuwgError] = useState<string | null>(null);

  // Sync gdy parent zmienia values (np. preload z bazy).
  useEffect(() => {
    setLatStr(latitude != null ? String(latitude) : '');
    setLngStr(longitude != null ? String(longitude) : '');
  }, [latitude, longitude]);

  // WGS84 changes — sanityzacja (przecinek→kropka, maska) + propagacja do parenta.
  // Drugie pole przekazujemy jako null gdy jeszcze puste/niepełne — inaczej parseFlot("") = NaN
  // wyciekał do rodzica i wyświetlał się jako "NaN" w drugim polu.
  const handleLatChange = (raw: string) => {
    const v = sanitizeDecimalInput(raw);
    setLatStr(v);
    const n = parseFloat(v);
    const other = parseFloat(lngStr);
    onChange(Number.isFinite(n) ? n : null, Number.isFinite(other) ? other : null);
  };
  const handleLngChange = (raw: string) => {
    const v = sanitizeDecimalInput(raw);
    setLngStr(v);
    const n = parseFloat(v);
    const other = parseFloat(latStr);
    onChange(Number.isFinite(other) ? other : null, Number.isFinite(n) ? n : null);
  };

  // PUWG 2000 → konwersja przy każdej zmianie
  useEffect(() => {
    if (coordFormat !== 'PUWG_2000') { setPuwgError(null); return; }
    const x = parseFloat(puwgX);
    const y = parseFloat(puwgY);
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      setPuwgError(null);
      return;
    }
    const zone = puwgZone === 'AUTO' ? detectZoneFromY(y) : puwgZone;
    if (zone == null) {
      setPuwgError('Nie udało się wykryć strefy z Y. Wpisz Y z prefiksem (np. 6 500 000 dla strefy 6) albo wybierz strefę ręcznie.');
      return;
    }
    const res = puwg2000ToWgs84(zone, x, y);
    if (!res) {
      setPuwgError('Konwersja nieudana — sprawdź czy X i Y są w prawidłowym zakresie dla terytorium Polski.');
      return;
    }
    setPuwgError(null);
    setLatStr(res.lat.toFixed(7));
    setLngStr(res.lng.toFixed(7));
    onChange(res.lat, res.lng);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coordFormat, puwgZone, puwgX, puwgY]);

  const stacked = layout === 'stacked';
  return (
    <>
      <div className={(stacked ? 'col-span-2 ' : '') + 'flex items-center gap-2 mt-1 mb-0.5'}>
        <span className="text-[11px] text-ink-500 font-semibold uppercase tracking-wide">{label}:</span>
        <div className="flex gap-1 bg-ink-50 p-0.5 rounded-md">
          <button type="button" onClick={() => setCoordFormat('WGS84')} disabled={disabled}
                  className={'btn btn-xs ' + (coordFormat === 'WGS84' ? 'btn-primary' : 'btn-ghost')}>
            WGS84 (stopnie)
          </button>
          <button type="button" onClick={() => setCoordFormat('PUWG_2000')} disabled={disabled}
                  className={'btn btn-xs ' + (coordFormat === 'PUWG_2000' ? 'btn-primary' : 'btn-ghost')}
                  title="Państwowy Układ Współrzędnych Geodezyjnych 2000 — używany na mapach EGiB / BDOT10k">
            PUWG 2000 (metry)
          </button>
        </div>
      </div>

      {coordFormat === 'WGS84' && (() => {
        const latV = coordValidity(latStr, 'lat');
        const lngV = coordValidity(lngStr, 'lng');
        const cls = (v: CoordValidity) => 'input font-mono'
          + (v.level === 'error' ? ' !border-red-400 focus:!border-red-500'
            : v.level === 'warn' ? ' !border-amber-400' : '');
        const hint = (v: CoordValidity) => v.msg
          ? <span className={'text-[11px] mt-0.5 ' + (v.level === 'error' ? 'text-red-600' : 'text-amber-600')}>{v.msg}</span>
          : null;
        return (
          <>
            <label className="field">
              <span className="field-label">Szerokość geogr.</span>
              <input value={latStr} onChange={e => handleLatChange(e.target.value)} disabled={disabled}
                     inputMode="decimal" className={cls(latV)} placeholder="52.23456"/>
              {hint(latV)}
            </label>
            <label className="field">
              <span className="field-label">Długość geogr.</span>
              <input value={lngStr} onChange={e => handleLngChange(e.target.value)} disabled={disabled}
                     inputMode="decimal" className={cls(lngV)} placeholder="21.01234"/>
              {hint(lngV)}
            </label>
          </>
        );
      })()}

      {coordFormat === 'PUWG_2000' && (
        <>
          <label className="field">
            <span className="field-label">Strefa PUWG 2000</span>
            <SearchableSelect value={String(puwgZone)}
                    onChange={v => setPuwgZone(v === 'AUTO' ? 'AUTO' : Number(v) as Puwg2000Zone)}
                    disabled={disabled}>
              <option value="AUTO">Auto (z prefiksu Y)</option>
              <option value="5">Strefa 5 (centralny południk 15°E)</option>
              <option value="6">Strefa 6 (18°E)</option>
              <option value="7">Strefa 7 (21°E)</option>
              <option value="8">Strefa 8 (24°E)</option>
            </SearchableSelect>
          </label>
          <div/>
          <label className="field">
            <span className="field-label">X (northing)</span>
            <input value={puwgX} onChange={e => setPuwgX(sanitizeDecimalInput(e.target.value))} disabled={disabled}
                   inputMode="decimal" className="input font-mono" placeholder="5790000.00"/>
          </label>
          <label className="field">
            <span className="field-label">Y (easting, z prefiksem strefy)</span>
            <input value={puwgY} onChange={e => setPuwgY(sanitizeDecimalInput(e.target.value))} disabled={disabled}
                   inputMode="decimal" className="input font-mono" placeholder="6500000.00"/>
          </label>
          <div className={stacked ? 'col-span-2' : ''}>
            {puwgError && (
              <div className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                {puwgError}
              </div>
            )}
            {!puwgError && latStr && lngStr && (
              <div className="text-[12px] text-ink-600 bg-ink-50 border border-ink-100 rounded p-2 font-mono">
                Przekonwertowano na WGS84: {Number(latStr).toFixed(6)}, {Number(lngStr).toFixed(6)}
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}
